import "./leafletSetup"; // Leaflet CSS + marker-icon bootstrap
/* eslint-disable react/prop-types */
/**
 * Survey-number (cadastral) layer for any react-leaflet <MapContainer>.
 *
 *  - TELANGANA: every survey parcel from our own database (`/api/property/
 *    cadastral`, filled on demand from TGRAC), plus Hyderabad town-survey
 *    parcels (TS No / Block / Ward), drawn as outlines with numbers.
 *  - ANDHRA PRADESH: APSAC's statewide cadastral map tiles (they carry the
 *    numbers from zoom 16), with Bhuvan's as a fallback; a tap looks the
 *    parcel up live, with its village / mandal / district.
 *  - A "Find Sy.No" search (Telangana): pick the village, type the survey number,
 *    and the map zooms to the parcel(s).
 *
 * Everything draws from zoom 15 (the ~300 m scale-bar view). Boundaries are
 * indicative, not a legal survey — every popup and the search panel say so.
 *
 * Drop it in as a child, gated by your own overlay toggle:
 *   {surveyOn && <SurveyNumberOverlay interactive />}
 * `interactive` turns on tap-to-identify. Leave it off on maps where a tap
 * already means something else (drawing a plot, dropping a pin, adding).
 */
import { useEffect, useRef, useState } from "react";
import { useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import ReactDOM from "react-dom";
import { Search } from "lucide-react";
import {
  cadastralApi,
  AP_TILE_URL,
  AP_WMS_URL,
  AP_WMS_LAYER,
  lng2cellX,
  lat2cellY,
} from "../../utils/cadastralApi";

const PARCEL_MIN_ZOOM = 15;
const LABEL_MIN_ZOOM = 15;
// A number is drawn only where the parcel is big enough on screen to hold it,
// and never over another number (label boxes are checked, not just centres).
const LABEL_MIN_PX = { w: 22, h: 12 };
const MAX_SPAN = 0.068; // ~7 km around the centre at most, even on huge screens
const MAX_LABELS = 500;
// A cell still loading from the government server is asked for again after this.
const INCOMPLETE_RETRY_MS = 8000;

// Parcel edges are drawn "cased", like roads on a map: a JaaGa-navy line under
// a white one, readable on satellite, road map and terrain. Labels and the
// selected parcel use the brand navy and gold.
const CASING_STYLE = { color: "#153568", weight: 3.2, opacity: 0.55, fill: false };
const PARCEL_STYLE = { color: "#ffffff", weight: 1.3, opacity: 0.95, fill: true, fillOpacity: 0.02 };
const PARCEL_HOVER = { color: "#f0a500", weight: 2.4, fillColor: "#f0a500", fillOpacity: 0.12 };
const SELECTED_STYLE = { color: "#f0a500", weight: 3, opacity: 1, fillColor: "#f0a500", fillOpacity: 0.18 };

const DISCLAIMER = "Indicative boundary — not a legal survey. Verify with the certified village map / FMB.";

// Bhuvan's WMS speaks EPSG:900913 (the old name for Web Mercator).
const BHUVAN_CRS = L.extend({}, L.CRS.EPSG3857, { code: "EPSG:900913" });

if (typeof document !== "undefined" && !document.getElementById("jg-cadastral-style")) {
  const style = document.createElement("style");
  style.id = "jg-cadastral-style";
  style.textContent = `
.jg-sy-label{background:rgba(21,53,104,.88);color:#fff;border:0;border-radius:6px;box-shadow:0 1px 3px rgba(0,0,0,.35);
  padding:1px 6px;font:700 11px/1.4 system-ui,sans-serif;white-space:nowrap}
.jg-sy-label.jg-ts{background:rgba(120,72,0,.9)}
.jg-sy-label:before{display:none}
.jg-sy-popup .leaflet-popup-content{margin:10px 12px;font:13px/1.45 system-ui,sans-serif;color:#1f2937}
.jg-ap-cadastral{filter:drop-shadow(0 0 1px #fff) drop-shadow(0 0 1px #fff) saturate(1.6)}
.jg-ap-town{filter:contrast(1.5) brightness(.85) drop-shadow(0 0 1px #fff)}`;
  document.head.appendChild(style);
}

const esc = (s) =>
  String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);

function headline(p) {
  if (p.kind === "town") {
    return [`TS No. ${esc(p.sy) || "—"}`, p.block && `Block ${esc(p.block)}`, p.ward && `Ward ${esc(p.ward)}`]
      .filter(Boolean)
      .join(" · ");
  }
  return `Sy. No. ${esc(p.sy) || "—"}`;
}

function popupHtml(p, source) {
  const where = p.kind === "town" ? [p.mandal, p.district] : [p.village, p.mandal, p.district];
  const place = where.filter(Boolean).map(esc).join(", ");
  return (
    `<div style="font-size:15px;font-weight:800;color:#153568">${headline(p)}</div>` +
    (p.kind === "town" ? `<div style="font-size:11px;color:#78350f">Town survey number</div>` : "") +
    (place ? `<div>${place}</div>` : "") +
    (p.govt ? `<div style="margin-top:4px;font-size:11.5px;font-weight:700;color:#b45309">Marked as Government land in the source</div>` : "") +
    `<div style="margin-top:6px;font-size:11px;color:#64748b">${esc(DISCLAIMER)}` +
    (source ? `<br/>Source: ${esc(source)}` : "") +
    `</div>`
  );
}

// Map centre → which state's survey layer applies. Asked only from the zoom
// where parcels draw; while a new answer is pending after a long move the old
// one is dropped, so one state's layer is never shown over the other.
function useRegion(enabled) {
  const map = useMap();
  const [state, setState] = useState({ region: null, zoom: map.getZoom() });
  const timer = useRef();
  const ctrl = useRef();
  const askedAt = useRef(null);
  const check = () => {
    const zoom = map.getZoom();
    clearTimeout(timer.current);
    if (!enabled || zoom < PARCEL_MIN_ZOOM) {
      setState((s) => ({ ...s, zoom }));
      return;
    }
    const c = map.getCenter();
    if (askedAt.current && map.distance(askedAt.current, c) > 3000) {
      setState({ region: null, zoom });
    } else {
      setState((s) => ({ ...s, zoom }));
    }
    timer.current = setTimeout(async () => {
      ctrl.current?.abort();
      ctrl.current = new AbortController();
      try {
        const region = await cadastralApi.region(c.lat, c.lng, { signal: ctrl.current.signal });
        askedAt.current = c;
        setState({ region, zoom: map.getZoom() });
      } catch {
        /* keep what we have */
      }
    }, 300);
  };
  useMapEvents({ moveend: check, zoomend: check });
  useEffect(() => {
    check();
    return () => {
      clearTimeout(timer.current);
      ctrl.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
  return state;
}

// Telangana parcels for the view: its zoom-15 cells from our API (cached),
// drawn on one canvas renderer.
function TelanganaParcels({ interactive, onSelect }) {
  const map = useMap();
  const layerRef = useRef(null);
  const casingRef = useRef(null);
  const rendererRef = useRef(null);
  const timer = useRef();
  const retryTimer = useRef();
  const runRef = useRef(0);
  const drawnKey = useRef("");

  const clear = () => {
    layerRef.current?.remove();
    casingRef.current?.remove();
    layerRef.current = null;
    casingRef.current = null;
  };

  const load = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      if (map.getZoom() < PARCEL_MIN_ZOOM) return;
      const c = map.getCenter();
      const b = map.getBounds();
      const half = MAX_SPAN / 2;
      const x0 = lng2cellX(Math.max(b.getWest(), c.lng - half));
      const x1 = lng2cellX(Math.min(b.getEast(), c.lng + half));
      const y0 = lat2cellY(Math.min(b.getNorth(), c.lat + half));
      const y1 = lat2cellY(Math.max(b.getSouth(), c.lat - half));
      const run = ++runRef.current;
      const jobs = [];
      for (let x = x0; x <= x1; x++) {
        for (let y = y0; y <= y1; y++) jobs.push(cadastralApi.cell(x, y).catch(() => null));
      }
      const parts = await Promise.all(jobs);
      if (run !== runRef.current) return; // a newer view took over

      // A parcel crossing a cell edge comes back in each cell: keep it once.
      const byId = new Map();
      let source = null;
      let incomplete = false;
      for (const part of parts) {
        if (!part) {
          incomplete = true;
          continue;
        }
        if (part.complete === false) incomplete = true;
        source = source || part.source;
        for (const f of part.features || []) byId.set(f.id, f);
      }
      // Some cells still loading at the source: ask again shortly.
      clearTimeout(retryTimer.current);
      if (incomplete) retryTimer.current = setTimeout(load, INCOMPLETE_RETRY_MS);

      // Same parcels at the same zoom, viewport moved: labels only need
      // re-placing, so redraw only when something actually changed.
      const viewKey = `${x0}:${x1}:${y0}:${y1}:${map.getZoom()}:${byId.size}`;
      const panned = drawnKey.current === viewKey;
      drawnKey.current = viewKey;
      if (panned && layerRef.current) {
        placeLabels();
        return;
      }

      const fc = { type: "FeatureCollection", features: [...byId.values()] };
      if (!rendererRef.current) rendererRef.current = L.canvas({ padding: 0.3 });
      clear();
      casingRef.current = L.geoJSON(fc, {
        renderer: rendererRef.current,
        interactive: false,
        style: () => CASING_STYLE,
      }).addTo(map);
      layerRef.current = L.geoJSON(fc, {
        renderer: rendererRef.current,
        interactive,
        style: () => PARCEL_STYLE,
        onEachFeature: (f, layer) => {
          if (!interactive) return;
          layer.on("mouseover", () => layer.setStyle(PARCEL_HOVER));
          layer.on("mouseout", () => layer.setStyle(PARCEL_STYLE));
          layer.on("click", (e) => onSelect?.(f, source, e.latlng));
        },
      }).addTo(map);
      placeLabels();
    }, 300);
  };

  // Numbers only for parcels visible on screen, biggest first, never
  // overlapping (each label's own box is checked).
  const placeLabels = () => {
    const layer = layerRef.current;
    if (!layer) return;
    layer.eachLayer((l) => l.unbindTooltip());
    if (map.getZoom() < LABEL_MIN_ZOOM) return;
    const size = map.getSize();
    const cands = [];
    layer.eachLayer((l) => {
      const sy = l.feature?.properties?.sy;
      if (!sy) return;
      const bb = l.getBounds();
      const nw = map.latLngToContainerPoint(bb.getNorthWest());
      const se = map.latLngToContainerPoint(bb.getSouthEast());
      const w = Math.abs(se.x - nw.x);
      const h = Math.abs(se.y - nw.y);
      if (w < LABEL_MIN_PX.w || h < LABEL_MIN_PX.h) return;
      const c = map.latLngToContainerPoint(bb.getCenter());
      if (c.x < 0 || c.y < 0 || c.x > size.x || c.y > size.y) return;
      cands.push({ l, sy, c, area: w * h });
    });
    cands.sort((a, b) => b.area - a.area);
    const placed = [];
    let n = 0;
    for (const { l, sy, c } of cands) {
      if (n >= MAX_LABELS) break;
      const lw = 12 + sy.length * 7;
      const box = [c.x - lw / 2, c.y - 9, c.x + lw / 2, c.y + 9];
      if (placed.some((p) => box[0] < p[2] && box[2] > p[0] && box[1] < p[3] && box[3] > p[1])) continue;
      placed.push(box);
      n++;
      // Text node, never HTML: the number comes from an external source.
      const span = document.createElement("span");
      span.textContent = sy;
      const town = l.feature.properties.kind === "town";
      l.bindTooltip(span, {
        permanent: true,
        direction: "center",
        className: town ? "jg-sy-label jg-ts" : "jg-sy-label",
        interactive: false,
      });
    }
  };

  useMapEvents({ moveend: load, zoomend: load });
  useEffect(() => {
    load();
    return () => {
      clearTimeout(timer.current);
      clearTimeout(retryTimer.current);
      runRef.current++;
      clear();
      rendererRef.current?.remove();
      rendererRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interactive]);
  return null;
}

// Bhuvan draws AP parcels only at 1:10,000 or closer, so zoom-15 tiles are
// asked for at 512 px (zoom-16 detail) from its live WMS. Fallback only.
const AP_WMS_LIVE = "https://bhuvan-vec1.nrsc.gov.in/bhuvan/wms";
const AndhraWms = L.TileLayer.WMS.extend({
  getTileUrl(coords) {
    const url = L.TileLayer.WMS.prototype.getTileUrl.call(this, coords);
    if (coords.z >= 16) return url;
    return url
      .replace(AP_WMS_URL, AP_WMS_LIVE)
      .replace(/([?&])width=256/i, "$1width=512")
      .replace(/([?&])height=256/i, "$1height=512");
  },
});

// APSAC's town survey layers (Nellore, Guntur, Srikakulam towns) fill town
// cores the state cadastre leaves empty. They have no tile cache, so each tile
// is an ArcGIS "export" image of the tile's box.
const AP_TOWN_EXPORT =
  "https://apsac.ap.gov.in/gisserver/rest/services/MAUD_ULB/Vector_Data1/MapServer/export";
const R_MERC = 20037508.342789244;
const ApTownLayer = L.TileLayer.extend({
  getTileUrl(coords) {
    const n = 2 ** coords.z;
    const size = (2 * R_MERC) / n;
    const w = -R_MERC + coords.x * size;
    const north = R_MERC - coords.y * size;
    const params = new URLSearchParams({
      bbox: `${w},${north - size},${w + size},${north}`,
      bboxSR: "3857",
      imageSR: "3857",
      size: "256,256",
      layers: "show:4,6,21",
      format: "png32",
      transparent: "true",
      dpi: "96",
      f: "image",
    });
    return `${AP_TOWN_EXPORT}?${params}`;
  },
});

// Andhra Pradesh: APSAC's cadastral tiles (numbers from zoom 16), or Bhuvan's
// if APSAC's tiles keep failing, plus the town survey layers on top.
function AndhraTiles() {
  const map = useMap();
  const [useBhuvan, setUseBhuvan] = useState(false);
  useEffect(() => {
    let errors = 0;
    const layer = useBhuvan
      ? new AndhraWms(AP_WMS_URL, {
          layers: AP_WMS_LAYER,
          format: "image/png",
          transparent: true,
          version: "1.1.1",
          crs: BHUVAN_CRS,
          minZoom: PARCEL_MIN_ZOOM,
          maxZoom: 20,
          maxNativeZoom: 19,
          opacity: 0.9,
          attribution: "Survey boundaries © NRSC/ISRO Bhuvan",
        })
      : L.tileLayer(AP_TILE_URL, {
          minZoom: PARCEL_MIN_ZOOM,
          maxZoom: 20,
          maxNativeZoom: 19,
          className: "jg-ap-cadastral",
          attribution: "Survey boundaries © APSAC, Govt. of Andhra Pradesh",
        });
    if (!useBhuvan) {
      layer.on("tileerror", () => {
        errors++;
        if (errors >= 6) setUseBhuvan(true);
      });
    }
    layer.addTo(map);
    return () => layer.remove();
  }, [map, useBhuvan]);
  useEffect(() => {
    const town = new ApTownLayer("", {
      minZoom: PARCEL_MIN_ZOOM,
      maxZoom: 20,
      className: "jg-ap-town",
      // Only the three towns have data; skip tiles elsewhere.
      bounds: L.latLngBounds([14.3, 79.85], [18.4, 84.0]),
    }).addTo(map);
    return () => town.remove();
  }, [map]);
  return null;
}

// Tap anywhere (AP, or a Telangana spot not drawn yet) → ask what's there.
function TapIdentify({ onSelect }) {
  const map = useMap();
  useMapEvents({
    click: async (e) => {
      if (map.getZoom() < PARCEL_MIN_ZOOM) return;
      try {
        const out = await cadastralApi.identify(e.latlng.lat, e.latlng.lng);
        if (out?.parcel) onSelect(out.parcel, out.source, e.latlng);
      } catch {
        /* nothing to show */
      }
    },
  });
  return null;
}

// Gold outline + popup for the chosen parcel(s).
function useSelection() {
  const map = useMap();
  const ref = useRef({ layer: null, popup: null });
  useEffect(
    () => () => {
      ref.current.layer?.remove();
      ref.current.popup?.remove();
    },
    []
  );
  return (features, source, at) => {
    ref.current.layer?.remove();
    ref.current.popup?.remove();
    const list = Array.isArray(features) ? features : [features];
    if (!list.length) return;
    ref.current.layer = L.geoJSON({ type: "FeatureCollection", features: list }, {
      style: () => SELECTED_STYLE,
      interactive: false,
    }).addTo(map);
    const where = at || ref.current.layer.getBounds().getCenter();
    const first = list[0].properties || {};
    const html =
      list.length === 1
        ? popupHtml(first, source)
        : `<div style="font-size:15px;font-weight:800;color:#153568">${list.length} parcels: ` +
          `${list.map((f) => esc(f.properties?.sy)).join(", ")}</div>` +
          `<div>${[first.village, first.mandal, first.district].filter(Boolean).map(esc).join(", ")}</div>` +
          `<div style="margin-top:6px;font-size:11px;color:#64748b">${esc(DISCLAIMER)}` +
          (source ? `<br/>Source: ${esc(source)}` : "") +
          `</div>`;
    ref.current.popup = L.popup({ className: "jg-sy-popup", maxWidth: 280 }).setLatLng(where).setContent(html).openOn(map);
  };
}

// "Sy. No." search panel (Telangana): village → survey number → zoom.
function SurveySearch({ onFound }) {
  const map = useMap();
  const [container, setContainer] = useState(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [villages, setVillages] = useState([]);
  const [village, setVillage] = useState(null);
  const [sy, setSy] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    const div = L.DomUtil.create("div", "leaflet-control");
    const ctrl = new L.Control({ position: "topright" });
    ctrl.onAdd = () => div;
    ctrl.addTo(map);
    L.DomEvent.disableClickPropagation(div);
    L.DomEvent.disableScrollPropagation(div);
    setContainer(div);
    return () => ctrl.remove();
  }, [map]);

  // Village suggestions as the user types (our own database; no Google cost).
  useEffect(() => {
    if (village || q.trim().length < 2) {
      setVillages([]);
      return undefined;
    }
    const ac = new AbortController();
    const t = setTimeout(async () => {
      try {
        setVillages(await cadastralApi.villages(q.trim(), { signal: ac.signal }));
      } catch {
        /* ignore */
      }
    }, 300);
    return () => {
      clearTimeout(t);
      ac.abort();
    };
  }, [q, village]);

  const find = async () => {
    if (!village || !sy.trim() || busy) return;
    setBusy(true);
    setMsg("");
    try {
      const out = await cadastralApi.search(village.dmv, sy.trim());
      if (!out?.features?.length) {
        setMsg(`No Sy. No. ${sy.trim()} found in ${village.village}.`);
      } else {
        const [w, s, e, n] = out.bbox;
        map.fitBounds([[s, w], [n, e]], { padding: [60, 60], maxZoom: 18 });
        onFound(out.features, out.source);
      }
    } catch (err) {
      setMsg(err?.response?.data?.status?.message || "Search failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!container) return null;
  const box = {
    background: "white",
    border: "2px solid rgba(0,0,0,0.18)",
    borderRadius: 8,
    boxShadow: "0 1px 5px rgba(0,0,0,0.25)",
  };
  const input = {
    width: "100%",
    border: "1px solid #cbd5e1",
    borderRadius: 6,
    padding: "6px 8px",
    fontSize: 13,
    outline: "none",
  };
  return ReactDOM.createPortal(
    open ? (
      <div style={{ ...box, width: 260, padding: 10, marginTop: 6 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <b style={{ color: "#153568", fontSize: 13 }}>Find a survey number · Telangana</b>
          <button type="button" onClick={() => setOpen(false)} aria-label="Close" style={{ border: 0, background: "none", cursor: "pointer", color: "#94a3b8" }}>
            ✕
          </button>
        </div>
        <div style={{ position: "relative" }}>
          <input
            value={village ? `${village.village}, ${village.mandal}` : q}
            onChange={(e) => {
              // Editing a picked village starts a fresh search for what's typed.
              if (village) {
                setVillage(null);
                setQ(e.target.value.split(",")[0]);
              } else {
                setQ(e.target.value);
              }
              setMsg("");
            }}
            placeholder="Village or mandal"
            style={input}
          />
          {!village && villages.length > 0 && (
            <div style={{ position: "absolute", zIndex: 5, left: 0, right: 0, top: "100%", background: "white", border: "1px solid #e2e8f0", borderRadius: 6, maxHeight: 200, overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,.12)" }}>
              {villages.map((v) => (
                <button
                  key={v.dmv}
                  type="button"
                  onClick={() => {
                    setVillage(v);
                    setVillages([]);
                    setMsg("");
                    const [w, s, e, n] = v.bbox;
                    map.fitBounds([[s, w], [n, e]], { padding: [30, 30], maxZoom: 16 });
                  }}
                  style={{ display: "block", width: "100%", textAlign: "left", padding: "6px 8px", border: 0, background: "white", cursor: "pointer", fontSize: 12.5 }}
                >
                  <b>{v.village}</b>
                  <div style={{ color: "#64748b", fontSize: 11 }}>
                    {v.mandal}, {v.district}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
          <input
            value={sy}
            onChange={(e) => {
              setSy(e.target.value);
              setMsg("");
            }}
            onKeyDown={(e) => e.key === "Enter" && find()}
            placeholder="Survey no. e.g. 91/1"
            style={input}
          />
          <button
            type="button"
            onClick={find}
            disabled={!village || !sy.trim() || busy}
            style={{ background: "#153568", color: "white", border: 0, borderRadius: 6, padding: "0 12px", fontWeight: 700, fontSize: 13, cursor: "pointer", opacity: !village || !sy.trim() || busy ? 0.5 : 1 }}
          >
            {busy ? "…" : "Find"}
          </button>
        </div>
        {msg && <div style={{ marginTop: 6, fontSize: 12, color: "#b45309" }}>{msg}</div>}
        <div style={{ marginTop: 8, fontSize: 10.5, color: "#94a3b8", lineHeight: 1.35 }}>{DISCLAIMER}</div>
      </div>
    ) : (
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Find a survey number"
        style={{ ...box, height: 34, padding: "0 10px", marginTop: 6, cursor: "pointer", fontWeight: 800, fontSize: 12, color: "#153568" }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
          <Search size={13} strokeWidth={2.6} />
          Find Sy.No
        </span>
      </button>
    ),
    container
  );
}

function ZoomChip({ zoom }) {
  const map = useMap();
  const [container, setContainer] = useState(null);
  useEffect(() => {
    const div = L.DomUtil.create("div", "leaflet-control");
    const ctrl = new L.Control({ position: "topleft" });
    ctrl.onAdd = () => div;
    ctrl.addTo(map);
    setContainer(div);
    return () => ctrl.remove();
  }, [map]);
  if (!container || zoom >= PARCEL_MIN_ZOOM) return null;
  return ReactDOM.createPortal(
    <span style={{ background: "rgba(21,53,104,.9)", color: "white", borderRadius: 999, padding: "4px 10px", fontSize: 11.5, fontWeight: 600 }}>
      Zoom in to see survey numbers
    </span>,
    container
  );
}

/**
 * @param {boolean} enabled     Render the overlay (returns null when false).
 * @param {boolean} interactive Tap a parcel to see its survey number.
 * @param {boolean} showSearch  Show the "Find Sy.No" search button (Telangana).
 * @param {boolean} showChip    Show a "Zoom in to see survey numbers" chip.
 * @param {(s: {region, zoom}) => void} onCoverageChange  Optional callback.
 */
export default function SurveyNumberOverlay({
  enabled = true,
  interactive = false,
  showSearch = true,
  showChip = false,
  onCoverageChange,
}) {
  const { region, zoom } = useRegion(enabled);
  const select = useSelection();

  useEffect(() => {
    if (enabled) onCoverageChange?.({ region, zoom });
  }, [enabled, region, zoom, onCoverageChange]);

  if (!enabled) return null;
  const close = zoom >= PARCEL_MIN_ZOOM;
  return (
    <>
      {close && region === "telangana" && <TelanganaParcels interactive={interactive} onSelect={select} />}
      {close && region === "andhra-pradesh" && <AndhraTiles />}
      {interactive && close && region === "andhra-pradesh" && <TapIdentify onSelect={select} />}
      {showSearch && <SurveySearch onFound={select} />}
      {showChip && <ZoomChip zoom={zoom} />}
    </>
  );
}
